import React, { useEffect, useState } from 'react';
import ProgressBar from '../../../../ProgressBar';
import { CardFormatType, CardSizeType, LiveCardType } from '../../../../../types/decks';
import type { useActions } from '../../../../../hooks/useActions';
import { LiveCardLandscape } from './LiveCardLandscape';
import { LiveCardPortrait } from './LiveCardPortrait';
import { LiveCardMiniHero } from './LiveCardMiniHero';

export type CardProps = {
  card: LiveCardType;
  cardSize: CardSizeType;
  cardFormat: CardFormatType;
  actions?: ReturnType<typeof useActions>;
};

export function LiveCard(props: CardProps): React.ReactNode {
  switch (props.cardFormat) {
    case 'PORTRAIT':
      return <LiveCardPortrait {...props} />;
    case 'MINI_HERO':
      return <LiveCardMiniHero {...props} />;
    case 'LANDSCAPE':
      return <LiveCardLandscape {...props} />;
    default: {
      const _exhaustive: never = props.cardFormat;
      void _exhaustive;
      return <LiveCardLandscape {...props} />;
    }
  }
}

export function useResolvedLiveCardContent(card: LiveCardType, showNextProgram: boolean) {
  const hasNextProgramTitle = Boolean(card.nextProgramTitle);
  const hasNextProgramImage = Boolean(card.nextProgramImageUrl);
  const hasNextProgramLabels = Boolean(card.nextProgramLabels && card.nextProgramLabels.length > 0);
  const hasNextProgramContent = hasNextProgramTitle || hasNextProgramImage || hasNextProgramLabels;

  const hasReachedNextProgramStart = useNextProgramStartReached(
    hasNextProgramContent,
    card.nextProgramStart
  );

  const shouldShowNextProgram =
    hasNextProgramContent && hasReachedNextProgramStart && showNextProgram;

  const displayImageUrl = (shouldShowNextProgram ? card.nextProgramImageUrl : card.imageUrl) ?? '';
  const displayTitle = (shouldShowNextProgram ? card.nextProgramTitle : card.title) ?? '';
  const displayLabels = (shouldShowNextProgram ? card.nextProgramLabels : card.labels) ?? [];
  const displayButton = shouldShowNextProgram ? card.nextProgramButton : card.button;

  return {
    shouldShowNextProgram,
    hasNextProgramContent,
    displayImageUrl,
    displayTitle,
    displayLabels,
    displayButton,
  };
}

const MAX_NEXT_PROGRAM_DELAY_MS = 24 * 60 * 60 * 1000;

export function useNextProgramStartReached(
  hasNextProgramContent: boolean,
  nextProgramStart: number | null | undefined
): boolean {
  const [reached, setReached] = useState(false);

  useEffect(() => {
    if (!hasNextProgramContent) {
      setReached(false);
      return;
    }
    const rawDelayMs = nextProgramStart ?? 0;
    const isInvalidDelay = !Number.isFinite(rawDelayMs);
    const swapIsAllowedImmediately = isInvalidDelay || rawDelayMs <= 0;
    if (swapIsAllowedImmediately) {
      setReached(true);
      return;
    }
    const delayMs = Math.min(rawDelayMs, MAX_NEXT_PROGRAM_DELAY_MS);
    setReached(false);
    const id = setTimeout(() => setReached(true), delayMs);
    return () => clearTimeout(id);
  }, [hasNextProgramContent, nextProgramStart]);

  return reached;
}

export function ArrowRightIcon() {
  return (
    <svg
      width="14"
      style={{ marginRight: '4px', marginBottom: '4px' }}
      height="14"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M14.929 4.92896L13.515 6.34296L18.172 11H3C2.448 11 2 11.448 2 12C2 12.552 2.448 13 3 13H18.172L13.515 17.657L14.929 19.071L22 12L14.929 4.92896Z"
      />
    </svg>
  );
}

export type Progress = NonNullable<LiveCardType['progress']>;

export function MovingProgres({ progress }: { progress: Progress }) {
  const [p, setP] = useState(progress.percentage);

  useEffect(() => {
    setP(progress.percentage ?? 0);
  }, [progress.percentage]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: setP should not be needed, but is kept to not introduce behavior changes while moving to Biome from ESLint
  useEffect(() => {
    if (progress.progressPerMinute === 0) {
      return;
    }

    const interval = setInterval(() => {
      setP(_p => {
        const newP = _p + progress.progressPerMinute;
        if (newP >= 100) {
          return 100;
        }
        return newP;
      });
    }, 1000 * 60);

    return () => clearInterval(interval);
  }, [setP, progress.progressPerMinute]);
  return <ProgressBar progress={p} />;
}

type ButtonLike = NonNullable<LiveCardType['button']>;

export function LiveCardButtonView({
  button,
  active,
  progress,
}: {
  button: ButtonLike;
  active?: boolean;
  progress?: Progress | null;
}) {
  const color = active && button.focusedTextColor ? button.focusedTextColor : button.textColor;

  const ICON_ONLY_BUTTON_SIZE = 24;

  const buttonOnly = !button.title?.trim() && !progress;

  return (
    <div
      style={{
        color: color ?? undefined,
        width: buttonOnly ? ICON_ONLY_BUTTON_SIZE : undefined,
      }}
      className="absolute h-6 px-3 flex left-3 bottom-3 z-1 rounded-full items-center justify-center text-marker-xs overflow-hidden"
    >
      <div
        style={{
          background:
            active && button.focusedBackgroundColor
              ? button.focusedBackgroundColor
              : (button.backgroundColor ?? undefined),
          opacity:
            active && button.focusedBackgroundOpacityInPct
              ? button.focusedBackgroundOpacityInPct / 100
              : (button.backgroundOpacityInPct ?? 100) / 100,
        }}
        className="absolute inset-0 z-[1]"
      />
      {button.iconUrl && (
        <img
          src={active && button.activeIconUrl ? button.activeIconUrl : button.iconUrl}
          alt=""
          aria-hidden="true"
          className={`z-[1] ${buttonOnly ? 'absolute' : 'relative mr-2'} h-3`}
        />
      )}
      {progress?.percentage != null && (
        <div
          className={`z-[1] h-1 w-4 relative rounded-full mr-2 overflow-hidden bg-opacity-white-400`}
        >
          <div
            style={{
              background: color ?? undefined,
              width: `${progress.percentage}%`,
            }}
            className="h-2 absolute left-0 top-0"
          />
        </div>
      )}
      {button.title && <span style={{ zIndex: 1 }}>{button.title}</span>}
    </div>
  );
}
